import type { Notification, NotificationPreference } from "@/store/slices/notificationsTypes";
import { inAppDeliveryAllowed } from "./preferences";
import type { ShowToastOptions } from "./show-unified-toast";

export type RecordingNoticeToastContext = {
  /** The mounted client's navigation (the router). Without it the toast carries no action. */
  onViewCall?: (callPath: string) => void;
  /** The user's stored delivery preferences. Omitted means none are known (allow). */
  preferences?: NotificationPreference[] | null;
};

/** The only route a notice may send the user to: one call page, from the server's own link. Anything else is dropped. */
const CALL_PATH = /^\/calls\/[A-Za-z0-9._~%-]+$/;

/**
 * The toast for a "Call / Recording" officer notice (joinNoticeAlerts.server writes it; the bell already holds it, so
 * the toast must not add a second entry). Null for every other notification, and null when the user has switched the
 * Call category (or its in-app delivery) off, the same rule notify() applies.
 */
export function recordingNoticeToastFor(notification: Notification, context: RecordingNoticeToastContext = {}): ShowToastOptions | null {
  if (notification.category !== "call") return null;
  const meta = notification.metadata as { topic?: unknown; kind?: unknown } | undefined;
  if (meta?.topic !== "recording_notice") return null;
  if (!inAppDeliveryAllowed("call", context.preferences)) return null;
  const missing = meta.kind === "missing";
  const callPath = typeof notification.actionUrl === "string" && CALL_PATH.test(notification.actionUrl) ? notification.actionUrl : null;
  const { onViewCall } = context;
  return {
    variant: missing ? "warning" : "info",
    title: notification.title,
    ...(notification.body ? { description: notification.body } : {}),
    categoryLabel: "Call / Recording",
    duration: missing ? 10_000 : 5_000,
    alreadyRecorded: true,
    id: `recording-notice-${notification.id}`,
    ...(callPath && onViewCall ? { actionLabel: notification.actionLabel || "View Call", onAction: () => onViewCall(callPath) } : {}),
  };
}
